import { useMemo, useState } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { ClipboardList, Filter, Plus } from 'lucide-react'

import type {
    StocktakeKind,
    StocktakeSessionTime,
    StocktakeStatus,
} from '@/lib/api/stocktake-api'
import { ClearFilterButton } from '@/components/common/clear-filter-button'
import { ListPageHeader } from '@/components/common/ListPageHeader'
import { RefreshButton } from '@/components/common/refresh-button'
import {
    STOCKTAKE_CARD_CODE_CLASS,
    StocktakeListCard,
} from '@/components/common/StocktakeListCard'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useIsMobile } from '@/hooks/use-is-mobile'
import {
    useStocktakeSessions,
    useStocktakeSessionsInfinite,
} from '@/hooks/use-stocktake-query'
import { formatDateVN } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute('/_app/_authed/_sidebar/stocktake/')({
    component: Page,
})

const KIND_LABELS: Record<StocktakeKind, () => string> = {
    purchase: () => m.stocktake_kind_purchase(),
    sorting: () => m.stocktake_kind_sorting(),
    scenting: () => m.stocktake_kind_scenting(),
    packaging: () => m.stocktake_kind_packaging(),
    product: () => m.stocktake_kind_product(),
}

const STATUS_LABELS: Record<StocktakeStatus, () => string> = {
    draft: () => m.stocktake_status_draft(),
    counting: () => m.stocktake_status_counting(),
    pending_approval: () => m.stocktake_status_pending_approval(),
    approved: () => m.stocktake_status_approved(),
    cancelled: () => m.stocktake_status_cancelled(),
}

const STATUS_COLORS: Record<StocktakeStatus, string> = {
    draft: 'bg-slate-100 text-slate-700',
    counting: 'bg-blue-100 text-blue-700',
    pending_approval: 'bg-amber-100 text-amber-800',
    approved: 'bg-green-100 text-green-700',
    cancelled: 'bg-red-100 text-red-700',
}

function Page() {
    const navigate = useNavigate()
    const [kindFilter, setKindFilter] = useState<StocktakeKind | 'all'>('all')
    const [statusFilter, setStatusFilter] = useState<StocktakeStatus | 'all'>(
        'all',
    )
    const [sessionTimeFilter, setSessionTimeFilter] = useState<
        StocktakeSessionTime | 'all'
    >('all')
    const [filterDialogOpen, setFilterDialogOpen] = useState(false)

    const isMobile = useIsMobile()

    const params = useMemo(
        () => ({
            kind: kindFilter === 'all' ? undefined : kindFilter,
            status: statusFilter === 'all' ? undefined : statusFilter,
            session_time:
                sessionTimeFilter === 'all' ? undefined : sessionTimeFilter,
            page: 1,
            per_page: 50,
        }),
        [kindFilter, statusFilter, sessionTimeFilter],
    )

    const { data, isLoading, isFetching, refetch } = useStocktakeSessions(
        params,
        { enabled: !isMobile },
    )
    const items = data?.items ?? []

    // Mobile: infinite (params bỏ page — hook tự quản)
    const infiniteParams = useMemo(
        () => ({
            per_page: 50,
            ...(kindFilter !== 'all' ? { kind: kindFilter } : {}),
            ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
            ...(sessionTimeFilter !== 'all'
                ? { session_time: sessionTimeFilter }
                : {}),
        }),
        [kindFilter, statusFilter, sessionTimeFilter],
    )
    const infinite = useStocktakeSessionsInfinite(infiniteParams, {
        enabled: isMobile,
    })
    const mobileItems = useMemo(
        () => infinite.data?.pages.flatMap((p) => p.items ?? []) ?? [],
        [infinite.data],
    )

    const filterActive =
        kindFilter !== 'all' ||
        statusFilter !== 'all' ||
        sessionTimeFilter !== 'all'
    const clearFilter = () => {
        setKindFilter('all')
        setStatusFilter('all')
        setSessionTimeFilter('all')
    }

    const renderFilters = () => (
        <>
            <div>
                <Label className="mb-1 block">
                    {m.stocktake_filter_kind_label()}
                </Label>
                <Select
                    value={kindFilter}
                    onValueChange={(v) =>
                        setKindFilter(v as StocktakeKind | 'all')
                    }
                >
                    <SelectTrigger className="w-full">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">
                            {m.stocktake_filter_all_kinds()}
                        </SelectItem>
                        {(
                            [
                                'purchase',
                                'sorting',
                                'scenting',
                                'packaging',
                                'product',
                            ] as StocktakeKind[]
                        ).map((k) => (
                            <SelectItem key={k} value={k}>
                                {KIND_LABELS[k]()}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
            <div>
                <Label className="mb-1 block">
                    {m.stocktake_filter_status_label()}
                </Label>
                <Select
                    value={statusFilter}
                    onValueChange={(v) =>
                        setStatusFilter(v as StocktakeStatus | 'all')
                    }
                >
                    <SelectTrigger className="w-full">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">
                            {m.stocktake_filter_all_status()}
                        </SelectItem>
                        {(
                            [
                                'draft',
                                'counting',
                                'pending_approval',
                                'approved',
                                'cancelled',
                            ] as StocktakeStatus[]
                        ).map((s) => (
                            <SelectItem key={s} value={s}>
                                {STATUS_LABELS[s]()}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
            <div>
                <Label className="mb-1 block">
                    {m.stocktake_filter_session_time_label()}
                </Label>
                <Select
                    value={sessionTimeFilter}
                    onValueChange={(v) =>
                        setSessionTimeFilter(v as StocktakeSessionTime | 'all')
                    }
                >
                    <SelectTrigger className="w-full">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">
                            {m.stocktake_filter_all_session_times()}
                        </SelectItem>
                        <SelectItem value="evening">
                            {m.stocktake_session_time_evening()}
                        </SelectItem>
                        <SelectItem value="morning">
                            {m.stocktake_session_time_morning()}
                        </SelectItem>
                    </SelectContent>
                </Select>
            </div>
        </>
    )

    return (
        <div className="space-y-4 p-4 md:p-6">
            <ListPageHeader
                icon={<ClipboardList className="size-5 shrink-0" />}
                title={m.stocktake_page_title()}
                subtitle={m.stocktake_page_subtitle()}
                utilityActions={
                    <>
                        <RefreshButton
                            onClick={() => void refetch()}
                            loading={isFetching}
                        />
                        <ClearFilterButton
                            active={filterActive}
                            onClear={clearFilter}
                        />
                        <Button
                            variant="outline"
                            size="icon"
                            className="md:hidden"
                            onClick={() => setFilterDialogOpen(true)}
                        >
                            <Filter className="size-4" />
                        </Button>
                    </>
                }
                primaryActions={
                    <Button
                        onClick={() => void navigate({ to: '/stocktake/new' })}
                        className="w-full"
                    >
                        <Plus className="size-4 mr-1" />
                        {m.stocktake_new_button()}
                    </Button>
                }
            />

            <div className="hidden md:grid gap-3 md:grid-cols-2">
                {renderFilters()}
            </div>

            <Dialog open={filterDialogOpen} onOpenChange={setFilterDialogOpen}>
                <DialogContent className="max-w-none w-screen h-[100svh] grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden rounded-none p-4 sm:h-auto sm:w-full sm:max-w-lg sm:rounded-xl sm:p-6">
                    <DialogHeader>
                        <DialogTitle>{m.stocktake_page_title()}</DialogTitle>
                    </DialogHeader>
                    <div className="grid content-start gap-3 overflow-y-auto py-2">
                        {renderFilters()}
                    </div>
                    <DialogFooter className="flex-row gap-2 pt-2 sm:justify-end">
                        <Button
                            variant="outline"
                            className="h-11 flex-1"
                            onClick={() => {
                                clearFilter()
                                setFilterDialogOpen(false)
                            }}
                        >
                            {m.common_cancel()}
                        </Button>
                        <Button
                            className="h-11 flex-1"
                            onClick={() => setFilterDialogOpen(false)}
                        >
                            {m.common_filter_apply()}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Mobile: card list + Xem thêm */}
            <div className="space-y-2 md:hidden">
                {infinite.isLoading ? (
                    <div className="py-8 text-center text-muted-foreground">
                        {m.common_loading()}
                    </div>
                ) : mobileItems.length === 0 ? (
                    <div className="py-8 text-center text-muted-foreground">
                        {m.stocktake_empty()}
                    </div>
                ) : (
                    <>
                        {mobileItems.map((s) => (
                            <StocktakeListCard
                                key={s.id}
                                onClick={() =>
                                    void navigate({
                                        to: '/stocktake/$id',
                                        params: { id: s.id },
                                    })
                                }
                                code={
                                    <Link
                                        to="/stocktake/$id"
                                        params={{ id: s.id }}
                                        className={STOCKTAKE_CARD_CODE_CLASS}
                                    >
                                        {s.code}
                                    </Link>
                                }
                                kindBadge={
                                    <Badge variant="outline">
                                        {KIND_LABELS[s.kind]()}
                                    </Badge>
                                }
                                statusBadge={
                                    <Badge className={STATUS_COLORS[s.status]}>
                                        {STATUS_LABELS[s.status]()}
                                    </Badge>
                                }
                                sessionDate={s.session_date}
                                totalLines={s.total_lines}
                                varianceLines={s.variance_lines}
                                createdBy={s.created_by_name ?? undefined}
                                approvedBy={s.approved_by_name ?? undefined}
                            />
                        ))}
                        {infinite.hasNextPage && (
                            <Button
                                variant="outline"
                                className="w-full h-11"
                                disabled={infinite.isFetchingNextPage}
                                onClick={() => void infinite.fetchNextPage()}
                            >
                                {infinite.isFetchingNextPage
                                    ? m.list_loading_more()
                                    : m.list_load_more()}
                            </Button>
                        )}
                    </>
                )}
            </div>

            {/* Desktop: table */}
            <div className="hidden md:block rounded-md border bg-card overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{m.stocktake_col_code()}</TableHead>
                            <TableHead>{m.stocktake_col_kind()}</TableHead>
                            <TableHead>{m.stocktake_col_date()}</TableHead>
                            <TableHead>
                                {m.stocktake_col_session_time()}
                            </TableHead>
                            <TableHead className="text-right">
                                {m.stocktake_col_total_lines()}
                            </TableHead>
                            <TableHead className="text-right">
                                {m.stocktake_col_variance()}
                            </TableHead>
                            <TableHead>{m.stocktake_col_status()}</TableHead>
                            <TableHead>{m.stocktake_col_creator()}</TableHead>
                            <TableHead>{m.stocktake_col_approver()}</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell
                                    colSpan={9}
                                    className="py-8 text-center text-muted-foreground"
                                >
                                    {m.common_loading()}
                                </TableCell>
                            </TableRow>
                        ) : items.length === 0 ? (
                            <TableRow>
                                <TableCell
                                    colSpan={9}
                                    className="py-8 text-center text-muted-foreground"
                                >
                                    {m.stocktake_empty()}
                                </TableCell>
                            </TableRow>
                        ) : (
                            items.map((s) => (
                                <TableRow
                                    key={s.id}
                                    className="cursor-pointer hover:bg-muted/50"
                                    onClick={() =>
                                        void navigate({
                                            to: '/stocktake/$id',
                                            params: { id: s.id },
                                        })
                                    }
                                >
                                    <TableCell className="font-mono">
                                        <Link
                                            to="/stocktake/$id"
                                            params={{ id: s.id }}
                                            className="text-primary hover:underline"
                                        >
                                            {s.code}
                                        </Link>
                                    </TableCell>
                                    <TableCell>
                                        <Badge variant="outline">
                                            {KIND_LABELS[s.kind]()}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="whitespace-nowrap">
                                        {formatDateVN(s.session_date)}
                                    </TableCell>
                                    <TableCell>
                                        <Badge
                                            variant="outline"
                                            className={
                                                s.session_time === 'morning'
                                                    ? 'border-emerald-200 text-emerald-700 bg-emerald-50'
                                                    : 'border-blue-200 text-blue-700 bg-blue-50'
                                            }
                                        >
                                            {s.session_time === 'morning'
                                                ? m.stocktake_session_time_short_morning()
                                                : m.stocktake_session_time_short_evening()}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="text-right tabular-nums">
                                        {s.total_lines}
                                    </TableCell>
                                    <TableCell className="text-right tabular-nums">
                                        {s.variance_lines > 0 ? (
                                            <span className="text-red-700 font-semibold">
                                                {s.variance_lines}
                                            </span>
                                        ) : (
                                            <span className="text-muted-foreground">
                                                —
                                            </span>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Badge
                                            className={STATUS_COLORS[s.status]}
                                        >
                                            {STATUS_LABELS[s.status]()}
                                        </Badge>
                                    </TableCell>
                                    <TableCell>
                                        {s.created_by_name ?? '—'}
                                    </TableCell>
                                    <TableCell>
                                        {s.approved_by_name ?? '—'}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>
        </div>
    )
}
